import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FiltersService from "../services/api/filters/filters.service";
import {
  COURSES_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { FilterProps } from "../types/AppTypes";
import CoursesService from "../services/api/courses/Courses.service";
import { useNotification } from "./notification.hook";
import { Course, CourseRequest } from "../types/api/courses.type";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import { useEffect } from "react";

interface useFormationCourseProps {
  filtersCourses: FilterProps[];
  paginationCourses: number;
  debounceSearchCourse: string;
}

const queryKeyGetCourses = "get-courses";

export const useFormationCourse = ({
  filtersCourses,
  paginationCourses,
  debounceSearchCourse,
}: useFormationCourseProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      queryKeyGetCourses,
      filtersCourses,
      paginationCourses,
      debounceSearchCourse,
    ],
    queryFn: () =>
      CoursesService.list(COURSES_PAGE_SIZE, paginationCourses, [
        ...filtersCourses,
        ...(debounceSearchCourse
          ? [{ key: "search", value: [debounceSearchCourse] }]
          : []),
      ]),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useFormationCourseFilters = () => {
  const { notification } = useNotification();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["get-filters-courses"],
    queryFn: FiltersService.listForCourses,
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading };
};

export const useSaveFormationCourse = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      body,
      record,
    }: {
      body: CourseRequest;
      record?: Course;
    }) => {
      if (record) {
        return CoursesService.update(record.id, body);
      } else {
        return CoursesService.add(body);
      }
    },
    onSuccess: (_, { record }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeyGetCourses] });
      const message = record
        ? DATA_UPDATED_SUCCESS_LABEL
        : DATA_ADDED_SUCCESS_LABEL;
      notification.success(message, undefined, "toast");
    },
    onError: (error) => {
      console.error(`${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useDeleteFormationCourse = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: number) => CoursesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyGetCourses] });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting formation course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};
